import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const MockTransactionsPage = () => {
  const [mockTrades, setMockTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [transactionFilter, setTransactionFilter] = useState('All');

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Fetch mock trades from backend
  const fetchMockTrades = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/mock-trades`);
      setMockTrades(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch mock trades:', error);
      setError('Failed to fetch mock trades');
      setMockTrades([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Generate transaction history from mock trades
  const generateTransactionHistory = () => {
    const transactionList = [];

    mockTrades.forEach((trade) => {
      // Add buy transaction
      if (hasBuyTransactionIcon(trade)) {
        transactionList.push({
          id: `buy-${trade.id || trade.token_address}`,
          type: 'buy',
          token: trade.symbol || trade.name || 'Unknown Token',
          tokenAddress: trade.token_address,
          amount: trade.original_amount || trade.amount || 0.015,
          solAmount: 0.015, // Standard mock trade amount (matches backend STATIC_DEPOSIT_AMOUNT)
          timestamp: trade.buy_time,
          price: trade.buy_price,
          color: 'red',
          description: `Bought ${trade.original_amount || trade.amount || 0.015} SOL worth of ${trade.symbol || trade.name || 'Unknown Token'} with 0.015 SOL`
        });
      }

      // Add sell transactions from partial_sales
      if (trade.partial_sales && trade.partial_sales.length > 0) {
        trade.partial_sales.forEach((sale, index) => {
          const isProfit = sale.profit_loss > 0;
          const isStopLoss = sale.reason && sale.reason.toLowerCase().includes('stop');
          const initialAmount = 0.015; // Initial investment amount (matches backend STATIC_DEPOSIT_AMOUNT)
          
          // Calculate actual SOL amount received based on profit/loss for partial sales
          let actualSolAmount;
          if (sale.amount && sale.amount > 0) {
            // If sale.amount is already calculated correctly, use it
            actualSolAmount = sale.amount;
          } else {
            // Calculate based on profit/loss percentage
            if (isProfit) {
              actualSolAmount = initialAmount * (1 + (sale.profit_loss / 100));
            } else {
              actualSolAmount = initialAmount * (1 + (sale.profit_loss / 100)); // profit_loss is negative for losses
            }
          }
          
          transactionList.push({
            id: `sell-${trade.id || trade.token_address}-${index}`,
            type: isProfit ? 'profit' : (isStopLoss ? 'stop_loss' : 'sell'),
            token: trade.symbol || trade.name || 'Unknown Token',
            tokenAddress: trade.token_address,
            amount: actualSolAmount,
            solAmount: actualSolAmount,
            timestamp: sale.timestamp,
            price: sale.price,
            profitLoss: sale.profit_loss,
            reason: sale.reason,
            color: isProfit ? 'green' : (isStopLoss ? 'yellow' : 'orange'),
            description: isProfit 
              ? `Took Profit from ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
              : isStopLoss 
                ? `Stopped Loss of ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
                : `Sold ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
          });
        });
      }
      
      // Add sell transactions for completed trades without partial_sales
      else if (trade.status === 'completed' && trade.sell_time && trade.sell_transaction_id) {
        const profitLoss = trade.profit_loss || 0;
        const isProfit = profitLoss > 0;
        const isStopLoss = trade.reason && trade.reason.toLowerCase().includes('stop');
        const initialAmount = 0.015; // Initial investment amount (matches backend STATIC_DEPOSIT_AMOUNT)
        
        // Calculate actual SOL amount received based on profit/loss
        let actualSolAmount;
        if (isProfit) {
          // For profit: initial amount + profit amount
          actualSolAmount = initialAmount * (1 + (profitLoss / 100));
        } else {
          // For loss: initial amount - loss amount
          actualSolAmount = initialAmount * (1 + (profitLoss / 100)); // profitLoss is negative for losses
        }
        
        transactionList.push({
          id: `sell-${trade.id || trade.token_address}`,
          type: isProfit ? 'profit' : (isStopLoss ? 'stop_loss' : 'sell'),
          token: trade.symbol || trade.name || 'Unknown Token',
          tokenAddress: trade.token_address,
          amount: actualSolAmount,
          solAmount: actualSolAmount,
          timestamp: trade.sell_time,
          price: trade.sell_price,
          profitLoss: profitLoss,
          reason: trade.reason,
          color: isProfit ? 'green' : (isStopLoss ? 'yellow' : 'orange'),
          description: isProfit 
            ? `Took Profit from ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
            : isStopLoss 
              ? `Stopped Loss of ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
              : `Sold ${trade.symbol || trade.name || 'Unknown Token'} and got ${actualSolAmount.toFixed(6)} SOL`
        });
      }
    });

    // Add deposit transactions from localStorage
    const storedDeposits = JSON.parse(localStorage.getItem('mockDeposits') || '[]');
    storedDeposits.forEach((deposit) => {
      transactionList.push({
        id: `deposit-${deposit.id}`,
        type: 'deposit',
        token: 'SOL',
        tokenAddress: 'SOL',
        amount: deposit.amount,
        solAmount: deposit.amount,
        timestamp: deposit.timestamp,
        price: null,
        color: 'blue',
        description: `Deposited ${deposit.amount} SOL to Mock Portfolio`
      });
    });

    // Sort by timestamp (newest first)
    return transactionList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Format USD value
  const formatUsdValue = (solAmount) => {
    if (!solAmount || !solPrice) return '$0.00';
    const usdValue = parseFloat(solAmount) * solPrice;
    return `$${usdValue.toFixed(2)}`;
  };

  // Format time to UTC+1
  const formatTimeUTC1 = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    date.setHours(date.getHours() + 1);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Get color classes for transaction types
  const getTransactionColorClasses = (color) => {
    switch (color) {
      case 'red':
        return 'bg-red-900/20 border-red-500/30 text-red-400';
      case 'green':
        return 'bg-green-900/20 border-green-500/30 text-green-400';
      case 'yellow':
        return 'bg-yellow-900/20 border-yellow-500/30 text-yellow-400';
      case 'blue':
        return 'bg-blue-900/20 border-blue-500/30 text-blue-400';
      case 'orange':
        return 'bg-orange-900/20 border-orange-500/30 text-orange-400';
      default:
        return 'bg-slate-900/20 border-slate-500/30 text-slate-400';
    }
  };

  // Get transaction icon
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'buy':
        return '📈';
      case 'profit':
        return '💰';
      case 'stop_loss':
        return '🛑';
      case 'sell':
        return '📉';
      case 'deposit':
        return '💳';
      default:
        return '📊';
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMockTrades(true); // Initial load
    fetchSolPrice(); // Fetch SOL price
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchMockTrades(false); // Background refresh
    }, 5000);
    
    // Set up SOL price refresh every 30 seconds
    const solPriceInterval = setInterval(() => {
      fetchSolPrice();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearInterval(solPriceInterval);
    };
  }, []);

  // Filter transactions by type
  const filterTransactions = (transactions, filter) => {
    if (filter === 'All') return transactions;
    
    // Map filter names to transaction types
    const filterMap = {
      'Buy Transaction': 'buy',
      'Profit Transaction': 'profit',
      'Stop Loss Transaction': 'stop_loss',
      'Deposit Transaction': 'deposit',
      'Sell Transaction': 'sell'
    };
    
    const transactionType = filterMap[filter];
    if (!transactionType) return transactions;
    
    return transactions.filter(t => t.type === transactionType);
  };

  // Get filtered and paginated transactions
  const filteredTransactions = filterTransactions(transactions, transactionFilter);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

  // Update transactions when mock trades change
  useEffect(() => {
    if (mockTrades.length > 0) {
      setTransactions(generateTransactionHistory());
    }
  }, [mockTrades]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [transactionFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 text-lg">{error}</p>
        <button 
          onClick={fetchMockTrades}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mock Transactions Header */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Mock Transaction History</h2>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-400">
            SIMULATION MODE
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Transactions</div>
            <div className="text-2xl font-bold text-blue-400">
              {transactions.length}
            </div>
            <div className="text-xs text-slate-500 mt-1">All Mock Transactions</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Buy Transactions</div>
            <div className="text-2xl font-bold text-red-400">
              {transactions.filter(t => t.type === 'buy').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Mock Purchases</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Profit Transactions</div>
            <div className="text-2xl font-bold text-green-400">
              {transactions.filter(t => t.type === 'profit').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Mock Profits</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Stop Loss</div>
            <div className="text-2xl font-bold text-yellow-400">
              {transactions.filter(t => t.type === 'stop_loss').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Mock Stop Losses</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Deposit Transactions</div>
            <div className="text-2xl font-bold text-blue-400">
              {transactions.filter(t => t.type === 'deposit').length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Mock Deposits</div>
          </div>
        </div>
      </div>

      {/* Transaction Filter Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h2 className="text-2xl font-bold text-white mb-4 lg:mb-0">Transaction History</h2>
        
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {['All', 'Buy Transaction', 'Profit Transaction', 'Stop Loss Transaction', 'Deposit Transaction'].map((filter) => (
            <button
              key={filter}
              onClick={() => {
                setTransactionFilter(filter);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                transactionFilter === filter
                  ? 'text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
              style={transactionFilter === filter ? { backgroundColor: '#22c55e' } : {}}
            >
              {filter} ({transactions.filter(t => {
                if (filter === 'All') return true;
                const filterMap = {
                  'Buy Transaction': 'buy',
                  'Profit Transaction': 'profit',
                  'Stop Loss Transaction': 'stop_loss',
                  'Deposit Transaction': 'deposit',
                  'Sell Transaction': 'sell'
                };
                const transactionType = filterMap[filter];
                return transactionType && t.type === transactionType;
              }).length})
            </button>
          ))}
        </div>
      </div>

      {/* Items Per Page Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-400">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
            <option value={70}>70</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <span className="text-sm text-slate-400">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
        </span>
      </div>

      {/* Mock Trading Info Banner */}
      <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-yellow-400 text-lg">📋</span>
          <div>
            <h4 className="text-yellow-400 font-semibold">Mock Transaction History</h4>
            <p className="text-yellow-300 text-sm">
              This page shows all simulated transactions from your mock trading activities. 
              All transactions are simulated and marked with appropriate color coding.
            </p>
            <p className="text-yellow-200 text-xs mt-1">
              Red = Buy, Green = Profit, Yellow = Stop Loss, Blue = Deposit
            </p>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      {filteredTransactions.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-400 text-lg">No transactions found for "{transactionFilter}" filter</p>
          <p className="text-slate-500 text-sm mt-2">Start mock trading or make deposits to see transaction history</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedTransactions.map((transaction, index) => (
            <div 
              key={transaction.id} 
              className={`backdrop-blur-sm rounded-xl p-6 border transition-all duration-300 hover:scale-[1.02] ${getTransactionColorClasses(transaction.color)}`}
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl">
                    {getTransactionIcon(transaction.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {transaction.description}
                    </h3>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="text-sm">
                        <span className="text-slate-400">Token:</span>
                        <span className="ml-1 font-medium">{transaction.token}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-400">Amount:</span>
                        <span className="ml-1 font-medium">{transaction.solAmount} SOL</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-400">USD:</span>
                        <span className="ml-1 font-medium">
                          {loadingSolPrice ? 'Loading...' : formatUsdValue(transaction.solAmount)}
                        </span>
                      </div>
                    </div>
                    {transaction.price && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">Price:</span>
                        <span className="ml-1 font-medium">${transaction.price}</span>
                      </div>
                    )}
                    {transaction.profitLoss !== undefined && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">P&L:</span>
                        <span className={`ml-1 font-medium ${transaction.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {transaction.profitLoss >= 0 ? '+' : ''}{transaction.profitLoss.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    {transaction.reason && (
                      <div className="text-sm mt-1">
                        <span className="text-slate-400">Reason:</span>
                        <span className="ml-1 font-medium">{transaction.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-slate-400 mb-1">Date & Time</div>
                  <div className="font-medium">
                    {formatTimeUTC1(transaction.timestamp)}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    UTC+1
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Previous
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    currentPage === pageNum
                      ? 'text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  style={currentPage === pageNum ? { backgroundColor: '#22c55e' } : {}}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Transaction Legend */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Transaction Type Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span className="text-slate-300">Buy Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-slate-300">Profit Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
            <span className="text-slate-300">Stop Loss Transaction</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span className="text-slate-300">Deposit Transaction</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTransactionsPage;
